import { Alert, Box, Card, CardContent, CardHeader, Collapse, Stack } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

import {
  BarChartIcon,
  IconAvatar,
  Select,
  Switches,
  TextField,
  Typography,
} from '@occc-app/design-system';
import type { ScanDataConfigurationFormValues } from '@occc/validation';

import {
  formatScanDataDiscountAllocation,
  parseScanDataDiscountAllocation,
  renderScanDataPlacementValue,
} from './utils';
import { SCAN_DATA_PLACEMENT_SELECT_ITEMS } from './constants';

type ScanDataConfigurationProps = {
  values?: ScanDataConfigurationFormValues;
  submitting?: boolean;
};

export const ScanDataConfiguration = ({
  values,
  submitting = false,
}: ScanDataConfigurationProps) => {
  return (
    <Card elevation={0} sx={{ mb: 0 }}>
      <CardHeader
        title={<Typography variant='subtitle1'>Scan Data Configuration</Typography>}
        subheader={
          <Typography variant='body2' color='textSecondary'>
            Configure where this offer appears in the Altria Scan Data file
          </Typography>
        }
        action={<Switches name='scanDataConfiguration.isEnabled' data={{ disabled: submitting }} />}
        avatar={<IconAvatar Icon={BarChartIcon} />}
        sx={{ py: 2, px: 3 }}
      />
      <Collapse in={values?.isEnabled} timeout='auto' unmountOnExit={false}>
        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={2} alignItems='flex-start' sx={{ width: '100%' }}>
            <Box
              sx={{
                width: { xs: '100%', sm: '50%' },
              }}
            >
              <Select
                name='scanDataConfiguration.fundingSource'
                label='Scan Data Placement'
                data={SCAN_DATA_PLACEMENT_SELECT_ITEMS}
                displayEmpty
                renderValue={renderScanDataPlacementValue}
                disabled={submitting}
              />
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems='flex-start'
              spacing={{ xs: 2, sm: 3 }}
            >
              <TextField
                name='scanDataConfiguration.description'
                label='Scan Data Description'
                placeholder='Scan Data Description'
                disabled={submitting}
              />
              <TextField
                name='scanDataConfiguration.discountAllocation'
                label='Discount Allocation'
                placeholder='Discount Allocation'
                disabled={submitting}
                fieldProps={{
                  format: formatScanDataDiscountAllocation,
                  parse: parseScanDataDiscountAllocation,
                }}
              />
            </Stack>
            <Alert
              icon={
                <InfoOutlined
                  aria-hidden
                  sx={(t) => ({
                    color: t.palette.primary.main,
                  })}
                />
              }
              severity='info'
              variant='standard'
              sx={(t) => ({
                alignItems: 'center',
                alignSelf: 'stretch',
                backgroundColor: alpha(t.palette.primary.main, 0.2),
                border: 'none',
                borderRadius: 1,
                boxShadow: 'none',
                color: t.palette.text.primary,
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                py: 1.5,
                px: 2,
                width: '100%',
                '& .MuiAlert-message': {
                  ...t.typography.body2,
                  color: t.palette.text.primary,
                  padding: 0,
                },
                '& .MuiAlert-icon': { marginRight: 0, padding: 0, opacity: 1 },
              })}
            >
              The selected placement determines which altria_line fields are populated in the Scan
              Data export for this offer. These values are automatically generated from your offer
              name and discount configuration above, however, they can be edited manually above.
            </Alert>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};
